import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {DialogMode, RecordDialog, RecordDialogConfig} from "@/shared/recording/recordDialog";
import {ProjectTimeInfoPanel} from "@/shared/projecttime/projectTimeInfoPanel";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {isProjectTimeDateTimeChangeAllowed} from "@/shared/securityHelper";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import {ValidationsListDialogComponent} from "@/shared/validations/validationsListComponent";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import * as AttachmentUtil from "@/shared/attachments/attachmentUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {mergeProperties, updateCutInfo} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDateTime, getISOZonedDateTime, normalizeTimeInput, Temporal} from "@/shared/utils/datefunctions";
import {AttachmentDownloadUrlResponse, AttachmentUploadUrlResponse, ReportProjectTime, Task, User} from "@/shared/timr";
import {RecordedEntryStatus} from "@/shared/timrTypes";


export class ProjectTimeDialog extends RecordDialog<ReportProjectTime> {

	private static readonly ATTACHMENT_ACCEPT_STRING = "image/*";
	private static readonly MAX_ATTACHMENT_COUNT = 20;
	private static readonly MAX_ATTACHMENT_SIZE = 10_000_000; // 10MB

	declare private taskDropdown: TaskDropdown;
	private selectedTaskId: number;
	private selectedTaskDescription: string;
	declare private projectTimeInfoPanel: ProjectTimeInfoPanel;
	declare private descriptionTypeahead: DescriptionTypeahead;
	private lastTask?: Task;
	private userAtLastTask?: number;
	declare private validationsList: ValidationsListDialogComponent;

	declare private attachmentGallery?: AttachmentGallery;


	constructor(config: RecordDialogConfig) {
		config = mergeProperties({
			title: {
				Create: "record.header.add",
				Edit: "record.header.edit",
				Show: "record.header",
				Duplicate: "record.header.duplicate"
			},
			users: []
		}, config);
		super(config);
	}

	protected initDialog() {
		super.initDialog();

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.dom.find(".descriptionTypeahead"),
			url: "filterProjectTimeDescriptions.json"
		});

		this.projectTimeInfoPanel = new ProjectTimeInfoPanel($("#projectTimeDialogInfoPanel"));

		this.initTaskDropdown();

		$("#projectTimeDialogBillable").change(() => {
			this.dom.find(`input[name="billable"]`).val(String($("#projectTimeDialogBillable").is(":checked")));
			this.updateBorder();
		});

		if (globals.savedValidationsEnabled) {
			this.validationsList = document.getElementById("projectTimeValidations") as ValidationsListDialogComponent;
		}

		this.attachmentGallery = document.getElementById("projectTimeAttachments") as AttachmentGallery;
		this.attachmentGallery?.setFileSelectorOptions({
			acceptString: ProjectTimeDialog.ATTACHMENT_ACCEPT_STRING,
			maxFiles: ProjectTimeDialog.MAX_ATTACHMENT_COUNT,
			maxFileSize: ProjectTimeDialog.MAX_ATTACHMENT_SIZE,
		});
	}

	public create(lastProjectTime?: Partial<ReportProjectTime>, createAnother = false) {
		super.create(lastProjectTime, createAnother);

		super.initCustomFields();

		if (!SecurityStatus.isAddProjectTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.projecttime.title",
				message: "account.expired.projecttime",
				escapeText: false
			});
			return;
		}

		$("#projectTimeDialogDescription").val("");
		$("#projectTimeDialogBreakTime").val("0");
		this.updateBreakTimeFieldState();

		this.disableBillableCheckbox(!globals.allowedToChangeBillable);

		this.updateProjectTimeInfoPanel(lastProjectTime);
		if (!createAnother) {
			if (lastProjectTime?.taskId) {
				this.taskDropdown.setSelection(lastProjectTime.taskId, lastProjectTime.taskName, lastProjectTime.taskBreadCrumbs);
				this.setBillable(lastProjectTime.taskBillable);
			} else {
				if (this.lastTask && (this.getSelectedUserId() === this.userAtLastTask)) {
					this.taskDropdown.setSelectedTask(this.lastTask);
					this.setBillable(this.lastTask.billable);
				} else {
					this.taskDropdown.setSelectedTask(null);
					this.setBillable(false);
				}
			}
		} else {
			this.descriptionTypeahead.setNeedsReset();
		}
		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), null);
			this.validationsList.validationsListItem = null;
		}

		this.attachmentGallery?.reset();

		this.showDialog();
	}

	protected showRecording(recording: ReportProjectTime, duplicate = false) {
		super.showRecording(recording, duplicate);

		this.updateCustomFields(recording, this.config.customFieldDefinitions);
		this.taskDropdown.setSelection(recording.taskId, recording.taskName, recording.taskBreadCrumbs);
		this.onTaskChanged();
		this.disableBillableCheckbox(!(globals.allowedToChangeBillable && recording.status === RecordedEntryStatus.Changeable));
		this.setBillable(recording.billable);

		$("#projectTimeDialogDescription").val(recording.description);

		if (duplicate) {
			this.dom.find(".breakTime").val(recording.breakTimeManual);
		}

		// in duplicate dialog we do not show validations
		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), duplicate ? null : recording);
			this.validationsList.validationsListItem = duplicate ? null : {
				recordId: recording.id,
				validations: recording.validations
			};
		}

		this.projectTimeInfoPanel.update(recording.taskId, recording.taskDescription, null, this.getSelectedUserId());

		if (!duplicate) {
			// We do not copy attachments when duplicating.
			// Somehow the old recording.id is still set, which would lead to the attachments of the original record being loaded.
			// Therefore, we only load attachments when not duplicating.
			this.loadAttachments(recording.id);
		}
	}

	protected override disableFields(disabled: boolean) {
		super.disableFields(disabled);
		if (this.attachmentGallery) {
			this.attachmentGallery.readonly = disabled;
		}
	}

	private loadAttachments(projectTimeId: number) {
		if (!this.attachmentGallery) {
			return;
		}

		this.attachmentGallery.reset();

		api("recording/getProjectTimeAttachments.json")
			.get({
				projectTimeId
			})
			.done((data: AttachmentDownloadUrlResponse[]) => {
				this.attachmentGallery.setExistingFiles(data);
			});
	}

	protected onSelectedUserChanged(user: User) {
		super.onSelectedUserChanged(user);
		this.taskDropdown.setUserId(user?.id);

		// if a Task has already been selected when the User changes we need to reload the Budget Info
		if (this.selectedTaskId) {
			this.projectTimeInfoPanel.update(this.selectedTaskId, this.selectedTaskDescription, null, user?.id);
		}
	}

	protected _delete() {
		const projectTimeId = $(this.config.dialogId).find(".id").val();
		if (!projectTimeId) {
			return;
		}
		const description = $(this.config.dialogId).find("#projectTimeDialogDescription").val() as string;
		const taskName = this.taskDropdown.getSelectedData() ? this.taskDropdown.getSelectedData().name : "";
		BootstrapModal.confirm({
			title: "projectTime.delete.title",
			message: messages.withArguments(
				"projectTime.delete.message",
				taskName,
				description ? " " + messages.withArguments("record.delete.message.description", description) : "",
				formatDateTime(this.parseStartDateTime())
			),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				api("recording/deleteProjectTime.json")
					.post({id: projectTimeId})
					.done(() => {
						modal.close();
						this.deleteDone();
					});
			},
			noAction: (modal) => {
				modal.close();
				this.deleteCancelled();
			}
		});
	}

	protected save() {
		if (!this.checkBeforeSubmit()) {
			this.dialog.resetLoadingButtons();
			return;
		}

		const saveAction = () => {
			const newFiles = this.attachmentGallery?.getFilesToUpload() || [];
			const attachmentFormData = this.attachmentGallery?.getSerializedArrayElements() || [];

			api("recording/saveProjectTime.json")
				.postForm($("#projectTimeDialogForm"), undefined, attachmentFormData)
				.then((response: AttachmentUploadUrlResponse[]) => {
					// Notifies the backend of the successful upload, no need to wait for the response / handle the promise
					// We want to confirm each individual upload as soon as it is done and not wait for all of them
					const confirmCallback = (attachmentId: string) => {
						void api("recording/confirmProjectTimeAttachmentUpload.json")
							.post({
								attachmentId: attachmentId
							});
					}

					// Upload to S3 once the backend has generated the upload URLs
					return AttachmentUtil.uploadToS3(newFiles, response, confirmCallback);
				})
				.then(_ => {
					if (this.dialogMode === DialogMode.Create && this.dialog.getCheckboxValue()) {
						this.saveRecordDone({
							end: getISOZonedDateTime(this.parseEndDateTime()),
							userId: this.getSelectedUserId()
						});
					} else {
						this.saveRecordDone();
					}
				})
				.catch(_ => {
					this.saveFailed();
				});
		};

		if (this.attachmentGallery && this.attachmentGallery.getAttachmentsToDelete().length > 0) {
			const fileNames = this.attachmentGallery.getAttachmentsToDelete().map((attachment) => attachment.metadata.fileName).join(", ");

			BootstrapModal.confirm({
				message: messages.withArguments("attachments.delete.confirmation", fileNames),
				yesAction: (modal) => {
					saveAction()
					modal.close()
				},
				noAction: (modal) => {
					this.dialog.resetLoadingButtons();
					modal.close();
				}
			});
		} else {
			saveAction();
		}
	}

	/**
	 * Validates the form before submitting. Returns false (and shows feedback) when the entry must not be sent to
	 * the backend, so the caller can abort and reset the loading buttons.
	 */
	private checkBeforeSubmit(): boolean {
		normalizeTimeInput($("#projectTimeDialogStartTime"));
		normalizeTimeInput($("#projectTimeDialogEndTime"));
		$("#projectTimeDialogBillable").focus();
		const startDateTime = this.parseStartDateTime();
		const endDateTime = this.parseEndDateTime();
		if (startDateTime == null) {
			BootstrapModal.error({message: "invalid.start.datetime"});
			return false;
		}
		const projectTimeDialogStartTimeZone = $("#projectTimeDialogStartTimeZone").val() as string;
		if (!globals.timeZonePattern.test(projectTimeDialogStartTimeZone)) {
			BootstrapModal.error({message: "invalid.start.timezone"});
			return false;
		}
		const projectTimeDialogStartDateTime = $("#projectTimeDialogStartDateTime");
		projectTimeDialogStartDateTime.val(getISOZonedDateTime(startDateTime));
		if (endDateTime == null) {
			BootstrapModal.error({message: "invalid.end.datetime"});
			return false;
		}
		const projectTimeDialogEndTimeZone = $("#projectTimeDialogEndTimeZone").val() as string;
		if (!globals.timeZonePattern.test(projectTimeDialogEndTimeZone)) {
			BootstrapModal.error({message: "invalid.end.timezone"});
			return false;
		}
		const projectTimeDialogEndDateTime = $("#projectTimeDialogEndDateTime");
		projectTimeDialogEndDateTime.val(getISOZonedDateTime(endDateTime));
		Input.removeFeedback($("#projectTimeDialogEndDate"));
		if (Temporal.ZonedDateTime.compare(startDateTime, endDateTime) > 0) {
			Input.addFeedback($("#projectTimeDialogEndDate"), "has-error", messages["saveRecord.endBeforeStart"]);
			return false;
		}
		const projectTimeDialogBreakTime = $("#projectTimeDialogBreakTime");
		projectTimeDialogBreakTime.val((projectTimeDialogBreakTime.val() as string).replace(/\D/g, ""));
		if ($("#projectTimeDialogUserFilter").is(":visible") && !$("#projectTimeDialogUserId").val()) {
			BootstrapModal.error({message: "saveRecord.user.required"});
			return false;
		}
		return true;
	}

	private initTaskDropdown() {
		this.taskDropdown = new TaskDropdown({
			rootElement: $("#projectTimeDialogTaskDropdown"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: messages["task.select.task"]
		}, {
			loadRootNodesURL: "recording/loadAllTasks.json",
			loadSubNodesURL: "recording/treeLoad.json",
			filterNodesURL: "recording/filterTasks.json"
		});
		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task | null) => {
			this.selectedTaskId = task != null ? task.id : null;
			this.selectedTaskDescription = task != null ? task.description : null;

			if (!globals.allowedToChangeBillable || this.dialogMode === DialogMode.Create) {
				this.disableBillableCheckbox(!globals.allowedToChangeBillable);
				this.setBillable(task != null && task.billable);
			}

			this.projectTimeInfoPanel.update(this.selectedTaskId, this.selectedTaskDescription, null, this.getSelectedUserId());
			this.onTaskChanged();
			this.lastTask = task;
			this.userAtLastTask = this.getSelectedUserId();
		});
	}

	private setBillable(billable: boolean) {
		$("#projectTimeDialogBillable").prop("checked", billable);
		this.dom.find(`input[name="billable"]`).val(String(billable));
		this.updateBorder();
	}

	private updateProjectTimeInfoPanel(lastProjectTime?: Partial<ReportProjectTime>) {
		if (lastProjectTime?.taskId) {
			this.projectTimeInfoPanel.update(lastProjectTime.taskId, lastProjectTime.taskDescription, null, this.getSelectedUserId());
		} else if (this.lastTask) {
			this.projectTimeInfoPanel.update(this.lastTask.id, this.lastTask.description, null, this.getSelectedUserId());
		} else {
			this.projectTimeInfoPanel.reset();
		}
	}

	private onTaskChanged() {
		this.descriptionTypeahead.setFilter({taskId: this.taskDropdown.getSelectedId()});
	}

	private disableBillableCheckbox(disabled: boolean) {
		this.dom.find(`input[name="billableCheckbox"]`).prop("disabled", disabled);
		this.dom.find(`label[for="projectTimeDialogBillable"]`).toggleClass("cursor-default", disabled);
	}

	protected updateBorder() {
		const billable = this.dom.find(`input[name="billable"]`).val() === "true";
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.toggleClass("border-BILLABLE", billable);
		modalContent.toggleClass("border-NOT_BILLABLE", !billable);
		modalContent.removeClass("border-NOT_INITIALIZED");
	}

	protected isDateTimeChangeAllowed(userId?: number): boolean {
		return isProjectTimeDateTimeChangeAllowed(false, userId || this.getSelectedUserId(), this.config.allowedUserIds);
	}

	public duplicate(recording: ReportProjectTime) {
		super.duplicate(recording);
		this.attachmentGallery?.reset();
	}

	protected getStatus(record: ReportProjectTime): RecordedEntryStatus {
		return record.status;
	}
}
